import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldAlert, BookOpenText, FileText, BarChart3, Activity, Brain, Lock, Zap, CheckCircle2, ChevronRight } from "lucide-react";
import { GlowBackdrop, GlassCard, GradientButton, GhostButton, SectionLabel } from "@/components/primitives";
import CountUp from "@/components/CountUp";

const features = [
  { icon: Brain, title: "AI Copilot", text: "ChatGPT-quality conversational AI with markdown, code highlighting and session memory across teams." },
  { icon: ShieldAlert, title: "Incident Intelligence", text: "Predict severity, auto-generate RCAs and trigger response playbooks in seconds." },
  { icon: Activity, title: "Historical Resolution Engine", text: "Match incoming incidents to past resolved ones — surface fix, engineer, and time-to-resolve instantly." },
  { icon: BookOpenText, title: "Knowledge Base", text: "Semantic search across SOPs, runbooks and HR policy with AI-powered Q&A." },
  { icon: FileText, title: "Document Intelligence", text: "Upload PDFs, DOCX, XLSX — get AI summaries, extraction and chat-with-document." },
  { icon: BarChart3, title: "Enterprise Analytics", text: "Real-time dashboards across departments with predictive forecasts." },
];

const logos = ["Northwind", "Aperture", "Vandelay", "Initech", "Globex", "Hooli", "Pied Piper", "Stark Co."];
const testimonials = [
  { quote: "SentinelAI cut our MTTR by 62% in the first quarter. The Historical Resolution Engine is uncanny.", who: "Marisol Diaz", role: "VP Engineering, Aperture" },
  { quote: "Finally a copilot that understands enterprise context — not a generic chatbot bolted onto our stack.", who: "Kenji Park", role: "Head of SRE, Globex" },
  { quote: "Onboarded 400 engineers in two weeks. The AI surfaces the exact runbook they need.", who: "Aria Voss", role: "CTO, Vandelay" },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="landing-page">
      <GlowBackdrop intense />

      {/* Top bar */}
      <header className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 grid place-items-center shadow-[0_0_20px_rgba(0,240,255,0.4)]">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold">SentinelAI</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Ops Copilot</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-300">
          <a href="#features" className="hover:text-white">Features</a>
          <a href="#workflow" className="hover:text-white">Workflow</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
          <a href="#faq" className="hover:text-white">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-login-link"><GhostButton>Sign in</GhostButton></Link>
          <Link to="/register" data-testid="landing-register-link"><GradientButton>Get Started <ArrowRight className="h-4 w-4" /></GradientButton></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="text-center mx-auto max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-soft text-xs font-mono uppercase tracking-wider text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" /> Enterprise AI · v1.0 · GA
          </div>
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-balance">
            The <span className="gradient-text">AI command center</span><br className="hidden sm:block" />
            for enterprise operations.
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto text-balance">
            SentinelAI fuses incident intelligence, historical resolution memory, knowledge search and a ChatGPT-grade copilot — purpose-built for SRE, security and operations teams.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" data-testid="hero-cta-primary"><GradientButton className="px-6 py-3">Start free trial <ArrowRight className="h-4 w-4" /></GradientButton></Link>
            <Link to="/login" data-testid="hero-cta-secondary"><GhostButton className="px-6 py-3">Live demo</GhostButton></Link>
          </div>
        </motion.div>

        {/* Dashboard preview */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 relative gradient-border rounded-[28px]"
        >
          <GlassCard hover={false} className="p-4 md:p-6 rounded-[28px]">
            <div className="flex items-center justify-between border-b border-white/5 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <span className="ml-3 text-xs font-mono text-slate-500">sentinel.ai / dashboard</span>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Live preview</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Incident Health", v: 92, c: "from-cyan-400 to-blue-500" },
                { label: "AI Confidence", v: 88, c: "from-blue-500 to-violet-500" },
                { label: "System Uptime", v: 99, c: "from-emerald-400 to-cyan-400" },
                { label: "Risk Score", v: 27, c: "from-violet-500 to-cyan-400" },
              ].map((k, i) => (
                <div key={i} className="glass-soft p-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{k.label}</div>
                  <div className="mt-1 font-mono text-3xl font-semibold"><CountUp value={k.v} suffix={k.label === "System Uptime" ? "%" : ""} /></div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${k.v}%` }} transition={{ duration: 1.2, delay: 0.5 + i * 0.1 }} className={`h-full bg-gradient-to-r ${k.c}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 glass-soft p-4">
                <SectionLabel>AI Activity Stream</SectionLabel>
                <div className="mt-3 space-y-2 text-sm">
                  {["Generated RCA for payments-api p99 spike","Matched incident to 3 historical resolutions","Auto-summarized Q4 Architecture Review.pdf","Drafted on-call handoff report"].map((t, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.15 }} className="flex items-center gap-2 text-slate-300">
                      <Sparkles className="h-3.5 w-3.5 text-cyan-300" /> {t}
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="glass-soft p-4">
                <SectionLabel>Open Incidents</SectionLabel>
                <div className="mt-3 space-y-2">
                  {[{t:"Latency spike on payments-api",s:"critical"},{t:"OAuth refresh failures",s:"high"},{t:"CDN cache miss anomaly",s:"medium"}].map((x,i)=>(
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="truncate">{x.t}</span>
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${x.s==="critical"?"bg-red-500/10 text-red-400 border-red-500/30":x.s==="high"?"bg-orange-500/10 text-orange-400 border-orange-500/30":"bg-blue-500/10 text-blue-400 border-blue-500/30"}`}>{x.s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Logos */}
        <div className="mt-16 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-slate-500">Trusted by operations teams at</div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-slate-400">
            {logos.map((l) => <span key={l} className="text-sm font-semibold">{l}</span>)}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: 62, suffix: "%", label: "Faster MTTR" },
            { v: 4800, label: "Incidents resolved" },
            { v: 99, suffix: "%", label: "Customer retention" },
            { v: 14, label: "Hours saved / engineer / week" },
          ].map((s, i) => (
            <GlassCard key={i} className="p-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="mt-2 font-mono text-4xl font-bold gradient-text"><CountUp value={s.v} suffix={s.suffix || ""} /></div>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-2xl">
          <SectionLabel>What's inside</SectionLabel>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">One platform. <span className="gradient-text">Every operations workflow.</span></h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <GlassCard className="p-6 h-full">
                <div className="h-10 w-10 rounded-xl bg-white/[0.04] grid place-items-center border border-white/10">
                  <f.icon className="h-5 w-5 text-cyan-300" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-slate-400">{f.text}</p>
                <div className="mt-4 text-xs text-cyan-300 inline-flex items-center gap-1">Learn more <ChevronRight className="h-3.5 w-3.5" /></div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionLabel>AI Workflow</SectionLabel>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold">From signal to resolution in <span className="gradient-text">under 90 seconds</span>.</h2>
        <div className="mt-10 grid md:grid-cols-4 gap-5">
          {[
            { icon: Zap, t: "Detect", d: "Ingest signals from alerts, logs, traces and tickets." },
            { icon: Brain, t: "Reason", d: "AI predicts severity, identifies root cause and matches history." },
            { icon: BookOpenText, t: "Recall", d: "Surface previous resolutions, runbooks and the engineers who fixed it." },
            { icon: CheckCircle2, t: "Resolve", d: "Auto-draft fix, notify owner, generate post-incident report." },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <GlassCard className="p-6 h-full">
                <div className="flex items-center justify-between">
                  <s.icon className="h-5 w-5 text-cyan-300" />
                  <span className="text-[10px] font-mono text-slate-500">STEP 0{i+1}</span>
                </div>
                <h3 className="mt-3 text-lg font-semibold">{s.t}</h3>
                <p className="mt-1 text-sm text-slate-400">{s.d}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionLabel>Customer voices</SectionLabel>
        <div className="mt-6 grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <GlassCard key={i} className="p-6">
              <p className="text-slate-200 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500" />
                <div className="leading-tight">
                  <div className="text-sm font-semibold">{t.who}</div>
                  <div className="text-[11px] text-slate-400">{t.role}</div>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionLabel>Pricing</SectionLabel>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold">Built for <span className="gradient-text">teams that ship at scale</span>.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { name: "Starter", price: "$0", tag: "5 users · Demo data", features: ["AI Copilot (limited)", "Incident dashboard", "Community support"] },
            { name: "Team", price: "$24", tag: "per seat / month", features: ["Unlimited Copilot", "Historical Resolution Engine", "Knowledge Base & Docs", "Priority support"], featured: true },
            { name: "Enterprise", price: "Custom", tag: "SSO, on-prem, audit", features: ["Dedicated workspace", "SOC2 + ISO27001", "Custom SLAs", "Solutions architect"] },
          ].map((p, i) => (
            <GlassCard key={i} className={`p-6 ${p.featured ? "ring-1 ring-cyan-400/30 shadow-[0_0_60px_rgba(0,240,255,0.15)]" : ""}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{p.name}</h3>
                {p.featured && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">Most popular</span>}
              </div>
              <div className="mt-3 font-mono text-4xl font-bold">{p.price}<span className="text-sm font-medium text-slate-400 ml-1">{p.tag}</span></div>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                {p.features.map((f, j) => <li key={j} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-cyan-300" /> {f}</li>)}
              </ul>
              <Link to="/register"><GradientButton className="mt-6 w-full">Get started</GradientButton></Link>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 py-20">
        <SectionLabel>FAQ</SectionLabel>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold">Questions, answered.</h2>
        <div className="mt-8 space-y-3">
          {[
            ["Is my data private?", "Yes. SentinelAI runs in your VPC or our SOC2-audited cloud. Your prompts and incidents never train shared models."],
            ["Which LLMs power the Copilot?", "Configurable: GPT-5.2, Claude Sonnet 4.5 and Gemini 3 Flash via Emergent's Universal Key, or bring your own."],
            ["Can it integrate with our existing tools?", "Pre-built connectors for PagerDuty, Slack, Jira, ServiceNow, Datadog, Splunk, GitHub and more."],
            ["How fast can we onboard?", "Most teams are live in under a week, including SSO and SCIM provisioning."],
          ].map(([q, a], i) => (
            <GlassCard key={i} className="p-5">
              <div className="text-sm font-semibold">{q}</div>
              <div className="mt-1 text-sm text-slate-400">{a}</div>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <GlassCard className="p-10 text-center gradient-border">
          <h3 className="text-3xl sm:text-4xl font-bold tracking-tight">Bring sentinel-grade AI <span className="gradient-text">to your operations team.</span></h3>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">Free 14-day trial. No credit card. Cancel anytime.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/register" data-testid="cta-bottom-register"><GradientButton className="px-6 py-3">Start free trial <ArrowRight className="h-4 w-4" /></GradientButton></Link>
            <Link to="/login"><GhostButton className="px-6 py-3"><Lock className="h-4 w-4" /> Sign in</GhostButton></Link>
          </div>
        </GlassCard>
      </section>

      {/* Footer */}
      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/5">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Sparkles className="h-4 w-4 text-cyan-300" /> SentinelAI · © {new Date().getFullYear()}
        </div>
        <div className="text-sm text-slate-300 text-center">
          <span className="gradient-text font-bold tracking-wide">Team Hack</span>
          <span className="text-slate-500"> · built by </span>
          <span className="text-slate-200 font-medium">Anu · Aravind · Narendar · Sahil</span>
        </div>
      </footer>
    </div>
  );
}
